import React, { useEffect, useRef, useState } from "react";
import anandLogo from "../assets/anand-technologies-logo.png";
import { useCatalogFamilies } from "../hooks/useCatalogFamilies";

const MOBILE_MENU_HASH = "#mobile-menu";
const MOBILE_PRODUCTS_HASH = "#mobile-products";

function Navbar({
  onProductsClick,
  onCategorySelect,
  onNavigateHome,
  navMenuRequest,
}) {
  const { families: productMenuColumns } = useCatalogFamilies();
  const [productsOpen, setProductsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [activeMobileFamily, setActiveMobileFamily] = useState(null);
  const closeTimerRef = useRef(null);
  const mobileProductsAreaRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const revealMobileMenu = () => {
    mobileMenuRef.current?.scrollTo?.({ top: 0, behavior: "auto" });
  };

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openProductsMenu = () => {
    clearCloseTimer();
    setProductsOpen(true);
  };

  const closeProductsMenu = () => {
    clearCloseTimer();
    setProductsOpen(false);
  };

  const dismissProductsMenuFocus = (focusTarget = null) => {
    closeProductsMenu();
    focusTarget?.blur?.();
  };

  const scheduleCloseProductsMenu = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setProductsOpen(false);
      closeTimerRef.current = null;
    }, 150);
  };

  const syncMobileMenuFromHash = () => {
    if (window.innerWidth > 768) {
      setMobileMenuOpen(false);
      setMobileProductsOpen(false);
      setActiveMobileFamily(null);
      return;
    }

    if (window.location.hash === MOBILE_PRODUCTS_HASH) {
      setMobileMenuOpen(true);
      setMobileProductsOpen(true);
      return;
    }

    if (window.location.hash === MOBILE_MENU_HASH) {
      setMobileMenuOpen(true);
      setMobileProductsOpen(false);
      setActiveMobileFamily(null);
      return;
    }

    setMobileMenuOpen(false);
    setMobileProductsOpen(false);
    setActiveMobileFamily(null);
  };

  useEffect(() => () => clearCloseTimer(), []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setMobileMenuOpen(false);
        setMobileProductsOpen(false);
      } else {
        syncMobileMenuFromHash();
      }
    };

    const handlePopState = () => {
      syncMobileMenuFromHash();
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        if (
          window.location.hash === MOBILE_PRODUCTS_HASH ||
          window.location.hash === MOBILE_MENU_HASH
        ) {
          window.history.back();
        } else {
          setMobileMenuOpen(false);
          setMobileProductsOpen(false);
        }
      }
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("hashchange", handlePopState);
    document.addEventListener("keydown", handleKeyDown);
    syncMobileMenuFromHash();

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("hashchange", handlePopState);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) {
      document.body.classList.remove("mobile-menu-open");
      return undefined;
    }

    document.body.classList.add("mobile-menu-open");
    revealMobileMenu();

    return () => {
      document.body.classList.remove("mobile-menu-open");
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!mobileProductsOpen) {
      setActiveMobileFamily(null);
    }
  }, [mobileProductsOpen]);

  useEffect(() => {
    if (!mobileMenuOpen || !mobileProductsOpen || !activeMobileFamily) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!mobileProductsAreaRef.current?.contains(event.target)) {
        setActiveMobileFamily(null);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [activeMobileFamily, mobileMenuOpen, mobileProductsOpen]);

  useEffect(() => {
    if (!navMenuRequest || navMenuRequest.type !== "mobile-products") {
      return;
    }

    if (window.innerWidth <= 768) {
      const nextUrl = `${window.location.pathname}${window.location.search}${MOBILE_PRODUCTS_HASH}`;
      window.history.pushState(window.history.state, "", nextUrl);
      syncMobileMenuFromHash();
    }
  }, [navMenuRequest]);

  const closeMobileMenu = () => {
    if (
      window.location.hash === MOBILE_PRODUCTS_HASH ||
      window.location.hash === MOBILE_MENU_HASH
    ) {
      window.history.back();
      return;
    }

    setMobileMenuOpen(false);
    setMobileProductsOpen(false);
  };

  const dismissMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileProductsOpen(false);
    document.body.classList.remove("mobile-menu-open");
  };

  const openMobileMenu = () => {
    const nextUrl = `${window.location.pathname}${window.location.search}${MOBILE_MENU_HASH}`;
    window.history.pushState(window.history.state, "", nextUrl);
    syncMobileMenuFromHash();
  };

  const toggleMobileMenu = () => {
    if (mobileMenuOpen) {
      closeMobileMenu();
      return;
    }

    openMobileMenu();
  };

  const toggleMobileProducts = () => {
    if (!mobileProductsOpen) {
      const nextUrl = `${window.location.pathname}${window.location.search}${MOBILE_PRODUCTS_HASH}`;
      window.history.pushState(window.history.state, "", nextUrl);
      syncMobileMenuFromHash();
      return;
    }

    if (window.location.hash === MOBILE_PRODUCTS_HASH) {
      window.history.back();
      return;
    }

    setMobileProductsOpen(false);
  };

  return (
    <nav>
      <a
        href="/"
        className="nav-logo"
        aria-label="Anand Technologies"
        onClick={(event) => {
          event.preventDefault();
          closeProductsMenu();
          dismissMobileMenu();
          onNavigateHome?.();
        }}
      >
        <img
          className="nav-logo-image"
          src={anandLogo}
          alt="Anand Technologies"
        />
      </a>

      <ul className="nav-links">
        <li
          className={`nav-item${productsOpen ? " is-open" : ""}`}
          onMouseEnter={openProductsMenu}
          onMouseLeave={scheduleCloseProductsMenu}
          onFocus={openProductsMenu}
          onBlur={scheduleCloseProductsMenu}
        >
          <a
            href="/products"
            onClick={(event) => {
              event.preventDefault();
              dismissProductsMenuFocus(event.currentTarget);
              onProductsClick();
            }}
          >
            Products
          </a>
          <div className="mega-menu">
            {productMenuColumns.map((column) => (
              <div className="mega-col" key={column.title}>
                <div className="mega-col-title">{column.title}</div>
                <ul className="mega-links">
                  {column.items.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        className="mega-link-btn"
                        onClick={(event) => {
                          dismissProductsMenuFocus(event.currentTarget);
                          const matchedCategory = item.selection || null;
                          if (matchedCategory) {
                            onCategorySelect(
                              matchedCategory,
                              column.title,
                              "home"
                            );
                          } else {
                            onProductsClick();
                          }
                        }}
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </li>
        <li>
          <a
            href="/#about"
            onClick={(event) => {
              event.preventDefault();
              closeProductsMenu();
              onNavigateHome?.("about");
            }}
          >
            About
          </a>
        </li>
        <li>
          <a
            href="/#categories"
            onClick={(event) => {
              event.preventDefault();
              closeProductsMenu();
              onNavigateHome?.("categories");
            }}
          >
            Categories
          </a>
        </li>
        <li>
          <a
            href="/#contact"
            onClick={(event) => {
              event.preventDefault();
              closeProductsMenu();
              onNavigateHome?.("contact");
            }}
          >
            Contact
          </a>
        </li>
      </ul>

      <div className="nav-actions">
        <a
          href="/#contact"
          className="btn-nav btn-nav-fill"
          onClick={(event) => {
            event.preventDefault();
            closeProductsMenu();
            dismissMobileMenu();
            onNavigateHome?.("contact");
          }}
        >
          Get Quote
        </a>
        <button
          type="button"
          className={`nav-mobile-toggle${mobileMenuOpen ? " is-open" : ""}`}
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-nav-menu"
          aria-label="Toggle navigation menu"
          onClick={toggleMobileMenu}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      <div
        className={`nav-mobile-menu${mobileMenuOpen ? " is-open" : ""}`}
        id="mobile-nav-menu"
        ref={mobileMenuRef}
      >
        <div className="nav-mobile-menu-head">
          <div className="nav-mobile-menu-title">Menu</div>
        </div>
        <div className={`nav-mobile-group${mobileProductsOpen ? " is-open" : ""}`}>
          <button
            type="button"
            className="nav-mobile-link nav-mobile-link-expand"
            aria-expanded={mobileProductsOpen}
            onClick={toggleMobileProducts}
          >
            <span>Products</span>
            <span className="nav-mobile-link-arrow" aria-hidden="true">
              {">"}
            </span>
          </button>
          <div className="nav-mobile-submenu">
            <div className="nav-mobile-submenu-groups" ref={mobileProductsAreaRef}>
              {productMenuColumns.map((column) => {
                const isFamilyOpen = activeMobileFamily === column.title;

                return (
                  <div
                    className={`nav-mobile-subgroup${
                      isFamilyOpen ? " is-open" : ""
                    }`}
                    key={column.title}
                  >
                    <button
                      type="button"
                      className="nav-mobile-subgroup-toggle"
                      aria-expanded={isFamilyOpen}
                      onClick={() =>
                        setActiveMobileFamily(
                          isFamilyOpen ? null : column.title
                        )
                      }
                    >
                      <span className="nav-mobile-subgroup-title">
                        {column.title}
                      </span>
                      <span
                        className="nav-mobile-subgroup-caret"
                        aria-hidden="true"
                      >
                        {">"}
                      </span>
                    </button>
                    <div className="nav-mobile-subgroup-links">
                      {column.items.map((item) => (
                        <button
                          type="button"
                          className="nav-mobile-sublink"
                          key={item.id}
                          onClick={() => {
                            const matchedCategory =
                              item.selection || null;
                            closeProductsMenu();
                            dismissMobileMenu();
                            if (matchedCategory) {
                              onCategorySelect(
                                matchedCategory,
                                column.title,
                                "home"
                              );
                            } else {
                              onProductsClick();
                            }
                          }}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <button
          type="button"
          className="nav-mobile-link"
          onClick={() => {
            closeProductsMenu();
            dismissMobileMenu();
            onNavigateHome?.("about");
          }}
        >
          About
        </button>
        <button
          type="button"
          className="nav-mobile-link"
          onClick={() => {
            closeProductsMenu();
            dismissMobileMenu();
            onNavigateHome?.("categories");
          }}
        >
          Categories
        </button>
        <button
          type="button"
          className="nav-mobile-link"
          onClick={() => {
            closeProductsMenu();
            dismissMobileMenu();
            onNavigateHome?.("contact");
          }}
        >
          Contact
        </button>
      </div>
      {mobileMenuOpen ? (
        <button
          type="button"
          className="nav-mobile-backdrop"
          aria-label="Close navigation menu"
          onClick={closeMobileMenu}
        />
      ) : null}
    </nav>
  );
}

export default Navbar;
